<template>
  <div class="load-order-view">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Détails de la commande</h1>
        <p>
          Consultez le contexte complet de cette commande, incluant les
          informations client, planning et logistique.
        </p>
      </div>
      <div class="header-actions">
        <button class="btn btn-outline" @click="edit">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="remove">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </header>

    <section class="page-body fade-in" v-if="record">
      <div class="summary-card">
        <div>
          <span>Référence</span>
          <strong>{{ record.reference || "—" }}</strong>
        </div>
        <div>
          <span>Statut</span>
          <strong
            class="badge"
            :class="`badge--${statusClass(getStatus(record))}`"
          >
            {{ getStatusLabel(record) }}
          </strong>
        </div>
        <div>
          <span>Date prévue</span>
          <strong>{{
            formatDate(
              record.date || record.scheduled_date || record.scheduledDate
            )
          }}</strong>
        </div>
        <div>
          <span>Montant total</span>
          <strong>{{
            formatCurrency(
              record.totalPriceTTC || record.total_amount || record.totalAmount
            )
          }}</strong>
        </div>
      </div>

      <div class="detail-sections">
        <section class="detail-card">
          <h2>Client</h2>
          <ul>
            <li>
              <span class="label">Nom du client</span>
              <span class="value">
                {{
                  record.customerFullName ||
                  record.customer?.name ||
                  record.customer_name ||
                  record.customerName ||
                  "—"
                }}
              </span>
            </li>
            <li>
              <span class="label">Référence client</span>
              <span class="value">
                {{
                  record.customer_reference ||
                  record.customerReference ||
                  record.customer?.reference ||
                  "—"
                }}
              </span>
            </li>
            <li
              v-if="
                record.customer?.phone ||
                record.customer_phone ||
                record.customerPhone
              "
            >
              <span class="label">Téléphone</span>
              <span class="value">
                {{
                  record.customer?.phone ||
                  record.customer_phone ||
                  record.customerPhone
                }}
              </span>
            </li>
          </ul>
        </section>

        <section class="detail-card">
          <h2>Logistique</h2>
          <ul>
            <li>
              <span class="label">Créé le</span>
              <span class="value">{{
                formatDate(record.created_at || record.createdAt)
              }}</span>
            </li>
            <li>
              <span class="label">Dernière mise à jour</span>
              <span class="value">{{
                formatDate(record.updated_at || record.updatedAt)
              }}</span>
            </li>
            <li
              v-if="record.userFullName || record.userName || record.created_by"
            >
              <span class="label">Créé par</span>
              <span class="value">{{
                record.userFullName || record.userName || record.created_by
              }}</span>
            </li>
            <li
              v-if="
                record.totalQuantity ||
                record.totalItems ||
                record.items_count ||
                record.items?.length ||
                record.orderItems?.length
              "
            >
              <span class="label">Nombre d'articles</span>
              <span class="value">
                {{
                  record.totalQuantity ||
                  record.totalItems ||
                  record.items_count ||
                  record.items?.length ||
                  record.orderItems?.length
                }}
              </span>
            </li>
          </ul>
        </section>

        <section class="detail-card">
          <h2>Notes</h2>
          <p class="notes">
            {{
              record.remark ||
              record.notes ||
              record.comment ||
              "Aucune note fournie pour cette commande."
            }}
          </p>
        </section>
      </div>

      <section
        class="detail-card"
        v-if="
          (record.orderItems && record.orderItems.length) ||
          (record.items && record.items.length)
        "
      >
        <h2>Articles</h2>
        <div class="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Désignation</th>
                <th>Quantité</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in record.orderItems || record.items || []"
                :key="item.id || item.reference"
              >
                <td>
                  {{
                    item.itemFullName ||
                    item.designation ||
                    item.reference ||
                    "Article"
                  }}
                  <small v-if="item.reference"> ({{ item.reference }}) </small>
                </td>
                <td>{{ formatNumber(item.quantity || item.qty) }}</td>
                <td>
                  {{
                    formatCurrency(
                      item.totalPriceTTC || item.total_price || item.total
                    )
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </section>

    <section class="loading-state" v-else>
      <div class="loader"></div>
      <p>Chargement de la commande…</p>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "LoadOrderShow",
  computed: {
    ...mapGetters("loadOrder", {
      record: "getCurrent",
    }),
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    edit() {
      if (!this.record) return;
      this.$router.push({
        name: "loadorders-edit",
        params: {
          reference: this.record.reference || this.$route.params.reference,
        },
      });
    },
    async remove() {
      if (!this.record) return;
      const reference = this.record.reference || this.$route.params.reference;
      const confirmed = await this.$confirm?.({
        title: "Supprimer la commande",
        message: "Cette commande sera définitivement supprimée. Continuer ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("loadOrder/destroy", reference);
        this.$toast?.success?.("Commande supprimée.");
        this.$router.push({ name: "loadorders-list" });
      } catch (error) {
        console.error("Erreur lors de la suppression du bon de charge", error);
        this.$toast?.error?.("Impossible de supprimer la commande.");
      }
    },
    async loadRecord() {
      try {
        const reference = this.$route.params.reference;
        await this.$store.dispatch("loadOrder/show", reference);
      } catch (error) {
        console.error("Erreur lors du chargement du bon de charge", error);
        this.$toast?.error?.("Impossible de charger la commande.");
        this.$router.replace({ name: "loadorders-list" });
      }
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString("fr-FR", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return value;
      }
    },
    formatCurrency(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatNumber(value) {
      const number = Number(value);
      if (Number.isNaN(number)) return value || "—";
      return number % 1 === 0 ? number.toString() : number.toFixed(2);
    },
    getStatus(record) {
      if (!record) return "draft";
      if (record.canceled_at || record.canceled_by) return "cancelled";
      if (record.validated === 1 || record.validationDateTime)
        return "completed";
      if (record.checked === 1 || record.checkedDateTime) return "in-progress";
      if (record.status) return record.status;
      return "draft";
    },
    getStatusLabel(record) {
      if (!record) return "Brouillon";
      if (record.canceled_at || record.canceled_by) return "Annulé";
      if (record.validated === 1 || record.validationDateTime) return "Terminé";
      if (record.checked === 1 || record.checkedDateTime) return "En cours";
      if (record.status) {
        // If status is already in French, return as is, otherwise translate
        const statusMap = {
          draft: "Brouillon",
          cancelled: "Annulé",
          canceled: "Annulé",
          completed: "Terminé",
          "in-progress": "En cours",
          "in progress": "En cours",
        };
        const lowerStatus =
          typeof record.status === "string"
            ? record.status.toLowerCase()
            : String(record.status).toLowerCase();
        return (
          statusMap[lowerStatus] ||
          (typeof record.status === "string"
            ? record.status.charAt(0).toUpperCase() + record.status.slice(1)
            : record.status)
        );
      }
      return "Brouillon";
    },
    statusClass(status = "") {
      const normalized =
        typeof status === "string"
          ? status.toLowerCase().replace(/\s+/g, "-")
          : String(status).toLowerCase();
      return normalized || "draft";
    },
  },
  mounted() {
    this.loadRecord();
  },
  beforeDestroy() {
    this.$store.dispatch("loadOrder/resetCurrent");
  },
};
</script>

<style scoped>
.load-order-view {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fbff 0%, #eef2ff 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.header-actions {
  margin-left: auto;
  display: flex;
  gap: 0.75rem;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.summary-card {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.08),
    rgba(79, 70, 229, 0.12)
  );
  color: #1e293b;
}

.summary-card span {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #475569;
}

.summary-card strong {
  font-size: 1.25rem;
}

.badge {
  padding: 0.3rem 0.9rem;
  border-radius: 999px;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.04em;
  background: rgba(148, 163, 184, 0.25);
  color: #334155;
}

.badge--in-progress {
  background: rgba(59, 130, 246, 0.2);
  color: #1d4ed8;
}

.badge--completed {
  background: rgba(34, 197, 94, 0.2);
  color: #0f766e;
}

.badge--cancelled {
  background: rgba(239, 68, 68, 0.2);
  color: #b91c1c;
}

.detail-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.detail-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 18px 32px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.detail-card h2 {
  margin: 0;
  font-size: 1.1rem;
  color: #0f172a;
}

.detail-card ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.75rem;
}

.label {
  display: block;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin-bottom: 0.2rem;
}

.value {
  color: #0f172a;
  font-weight: 600;
}

.notes {
  margin: 0;
  color: #475569;
  line-height: 1.6;
}

.table-wrapper {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 400px;
}

thead {
  background: rgba(37, 99, 235, 0.08);
}

th,
td {
  padding: 0.85rem 1rem;
  text-align: left;
  border-bottom: 1px solid rgba(148, 163, 184, 0.2);
}

tbody tr:hover {
  background: rgba(241, 245, 249, 0.5);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-outline {
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: white;
  color: #475569;
}

.btn-outline:hover {
  border-color: #4f46e5;
  color: #4f46e5;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .load-order-view {
    padding: 1.5rem 1.5rem 4rem;
  }
}

@media (max-width: 768px) {
  .load-order-view {
    padding: 1.25rem 1rem 4rem;
  }

  .page-header {
    flex-wrap: wrap;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }

  .page-body {
    padding: 1.5rem;
  }
}
</style>
